
import {Component, Vue, Watch} from "vue-property-decorator";
import ListSelectOverlay from "@/components/list/ListSelectOverlay.vue";
import ImportModal from "@/components/import/ImportModal.vue";
import ImportResults from "@/components/import/ImportResults.vue";
import UsersMosaicView from "@/components/users/UsersMosaicView.vue";
import UsersGroup from "@/views/front-office/UsersGroup.vue";
import AddContact from "@/components/users/AddContact.vue";
import AddSubordinate from "@/components/users/AddSubordinate.vue";
import {Getter} from "vuex-class";
import EditDefaultRights from "@/components/rights/EditDefaultRights.vue";
import { SCOPED_GROUP_PREFIX, ODM_DEFAULT_USER_GROUP_PREFIX, ROWS_PER_PAGE } from "@/utils/constants";
import MylobyAutocompleteMultiple from "@/components/generic/autocomplete-multiple.vue";

@Component({
    components: {
        ListSelectOverlay,
        ImportModal,
        ImportResults,
        UsersGroup,
        UsersMosaicView,
        AddContact,
        AddSubordinate,
        EditDefaultRights,
        MylobyAutocompleteMultiple,
    },
})
export default class Users extends Vue {

    get displayOneTab(): boolean {
        return (!this.canDoInEnvCompany("READ_SUBORDINATES") && !this.canDoInEnvCompany("CREATE_SUBORDINATES"))
          && !this.canDoInEnvCompany("MANAGE_USER_GROUPS");
    }
    public $refs!: { createUserGroupSelectUsersAutocompleteMulti: any };
    @Getter private envCompanyId: number|null;
    @Getter private canDoInEnvCompany: (right: string) => boolean;

    private selectedUsers: any[] = [];
    private users: object[] = [];
    private headers: object[] = [];
    private statsDialog: boolean = false;
    private groupDialog: boolean = false;
    private loadingCreate: boolean = false;
    private loading: boolean = false;
    private userSearch: string = null;
    private stats: any = null;
    private userGroupForm: any = {
        name: "",
        description: "",
        defaultForMissionOrder: false,
        scoped: false,
        users: [],
        disabled: false,
    };
    private usersGroups: object[] = [];
    private mosaicView: boolean = true;
    private pagination: any = {
        sortDesc: [true],
        sortBy: ["updatedAt"],
        search: "",
        page: 1,
        itemsPerPage: 20,
    };
    private currentTab: string = "contacts";
    private userGroupSelectable: any = [];
    private userGroupType: string = null;
    private userGroupUsers: any[] = [];
    private totalUsers: number = 0;
    private incrementPagination: number = 0;
    private manageRightsKey: number = 0;
    private companyId: number = null;
    private cantFetchUsers: boolean = false;
    private ODM_DEFAULT_USER_GROUP_PREFIX = ODM_DEFAULT_USER_GROUP_PREFIX;
    private SCOPED_GROUP_PREFIX = SCOPED_GROUP_PREFIX;
    private ROWS_PER_PAGE = ROWS_PER_PAGE;

    @Watch("pagination", {deep: true})
    @Watch("envCompanyId")
    public handlerQueryParams() {
      if (this.currentTab !== "groups") {
        this.reset();
      }
    }
    @Watch("userGroupType")
    public handlerNewGroupType(_, oldVal) {
        if (oldVal && !this.userGroupForm.scoped && this.$refs.createUserGroupSelectUsersAutocompleteMulti) {
            this.$refs.createUserGroupSelectUsersAutocompleteMulti.clear();
            this.$refs.createUserGroupSelectUsersAutocompleteMulti.getFirstItems();
        }
    }

    @Watch("userGroupForm.scoped")
    public async handlerScoped(val) {
        if (!this.selectedUsers?.length) {
            this.userGroupUsers = [];
        }
    }
    @Watch("userGroupUsers")
    public handlerNewGroupUsers(newVal: any[]) {
        this.userGroupForm.users = newVal;
    }

    @Watch("mosaicView")
    public mosaicViewHandler(val) {
        this.pagination.itemsPerPage = val ? 20 : 10;
    }

    private enterKeyPress(e) {
        if (e.keyCode === 13) {
            this.searchHandler();
        }
    }

    private searchHandler(): void {
        this.pagination.search = this.userSearch;
        this.pagination.page = 1;
    }

    private mounted() {
        this.headers = [
            {value: "firstName", sortable: true},
            {value: "lastName", sortable: true},
            {value: "email", sortable: true},
            {value: "specificCompanyName", sortable: true},
            {value: "job", sortable: true},
            {value: "phone", sortable: true},
        ];
        this.statsDialog = !!this.$route.params.data;
        this.stats = this.$route.params.data;
        if (this.$route.params.tab) {
            this.currentTab = this.$route.params.tab;
        }
        this.reset();
        this.scroll();
    }

    private changeTab(tab) {
        if (this.currentTab !== tab) {
            this.selectedUsers = [];
        }
        this.currentTab = tab;
        this.totalUsers = 0;
        this.pagination = {
            sortDesc: [true],
            sortBy: ["updatedAt"],
            search: "",
            page: 1,
            itemsPerPage: 20,
        };
        this.manageRightsKey++;
    }

    private clickRow(item) {
        return this.$router.push({name: "user", params: {id: item.id}});
    }

    private reset(pagination?: any): void {
        this.users = [];
        this.usersGroups = [];
        this.userGroupForm = {
        name: "",
        description: "",
        defaultForMissionOrder: false,
        scoped: false,
        users: [],
        disabled: false,
        };
        this.loading = true;
        const payload: any = pagination || this.pagination;
        this.cantFetchUsers = false;
        this.fetchData(payload)
            .then((res) => {
              if (res) {
                if (this.currentTab === "groups") {
                  this.usersGroups = res.list;
                } else {
                  this.users = res.users?.filter((user) => !!user?.id);
                }
                this.totalUsers = res.count;
              } else {
                this.cantFetchUsers = true;
              }
            })
            .catch((error) => {
                this.$store.commit("alerts/displayHttpError", { error });
            })
            .finally(() => {
              this.loading = false;
            });
        this.close();
    }

    private fetchData(pagination: any): Promise<any> {
        let p: Promise<any>;
        switch (this.currentTab) {
            case "contacts":
              p = this.getContacts(pagination);
              break;
            case "management":
              p = this.getSubordinates(pagination);
              break;
            case "groups":
              p = this.getUserGroups(pagination);
              break;
            default:
              p = Promise.reject();
        }
        return p;
    }

    private getContacts(query: any): Promise<any> {
        if (!this.canDoInEnvCompany("READ_CONTACTS")) {
            return Promise.resolve();
        }
        return this.$store.dispatch("companies/getContacts", {
            query,
            id: this.envCompanyId,
        });
    }

    private getSubordinates(query: any): Promise<any> {
        if (!this.canDoInEnvCompany("READ_SUBORDINATES")) {
            return Promise.resolve();
        }
        return this.$store.dispatch("companies/getSubordinates", {
            query,
            id: this.envCompanyId,
        });
    }

    private getUserGroups(payload: any): Promise<any> {
        if (!this.canDoInEnvCompany("MANAGE_USER_GROUPS")) {
            return Promise.resolve();
        }
        return this.$store.dispatch("companies/getUserGroups", {
            query: {payload,
                includeUsersCount: true,
            },
            id: payload.companyId || this.envCompanyId,
        });
      }

    private scroll(): void {
        window.onscroll = () => {
            const listElm = document.querySelector(".scroll-list");
            const isTargetPos = listElm?.clientHeight <= (document.documentElement.scrollTop + window.innerHeight);
            const nbElements: number = this.currentTab === "groups"
              ? this.usersGroups?.length
              : this.users?.length;
            const allElementsFetched = this.totalUsers === nbElements;
            if (isTargetPos && !this.loading && !allElementsFetched) {
                this.loading = true;
                this.incrementPagination++;
                this.cantFetchUsers = false;
                this.fetchData({
                    ...this.pagination,
                    page: this.pagination.page + this.incrementPagination,
                    companyId: this.envCompanyId,
                }).then((res) => {
                    if (res) {
                      if (this.currentTab === "groups") {
                        this.usersGroups.push(...res.list?.filter((g) => !!g?.id));
                      } else {
                        this.users.push(...res.users?.filter((u) => !!u?.id));
                      }
                      this.totalUsers = res.count;
                    } else {
                      this.cantFetchUsers = true;
                    }
                }).catch((error) => {
                    this.$store.commit("alerts/displayHttpError", { error });
                }).finally(() => {
                    this.loading = false;
                });
            }
        };
    }

    private deleteContacts() {
      if (confirm(this.$tc("contacts.deleteConfirmation", this.selectedUsers.length).toString())) {
          this.$store.dispatch("companies/deleteContacts", {
              companyId: this.envCompanyId,
              contactsIds: this.selectedUsers.map((user: any) => user.id),
          }).then((res) => {
            this.$store.commit("alerts/displaySuccess", {
              msg: this.$t("contacts.deleted"),
            });
            this.reset();
          }).catch((error) => {
              this.$store.commit("alerts/displayHttpError", { error });
          });
      }
    }

    private deleteManagers() {
      if (confirm(this.$tc("management.deleteConfirmation", this.selectedUsers.length).toString())) {
          this.$store.dispatch("companies/deleteSubordinates", {
              companyId: this.envCompanyId,
              subordinatesIds: this.selectedUsers.map((user: any) => user.id),
          }).then((res) => {
              this.$store.commit("alerts/displaySuccess", {
                msg: this.$t("management.deleted"),
              });
              this.reset();
          }).catch((error) => {
              this.$store.commit("alerts/displayHttpError", { error });
          });
      }
    }

    private selectAllUsers() {
      this.selectedUsers = this.users;
    }

    private deselectAllUsers() {
        this.selectedUsers = [];
    }

    private displayCreateUserGroupDialog() {
        this.selectedUsers = this.selectedUsers.filter((u) => u?.hasOwnProperty("email"));
        this.groupDialog = true;
        this.userGroupType = this.currentTab === "management" ? "subordinates" : "contacts";
        this.userGroupForm.disabled = !!this.selectedUsers.length && !this.selectedUsers?.[0]?.Users;
        this.userGroupUsers = this.selectedUsers.length ? this.selectedUsers : [];
    }

    private async createUserGroup() {
        this.loadingCreate = true;
        const group = {
        ...this.userGroupForm,
        type: this.userGroupType,
        };
        if (group.defaultForMissionOrder) {
          group.name = ODM_DEFAULT_USER_GROUP_PREFIX + group.name;
        }
        if (group.scoped) {
          group.name = SCOPED_GROUP_PREFIX + group.name;
        }
        this.$store.dispatch("userGroups/create", {
            userIds: this.userGroupUsers.map((u: any) => u.id),
            userGroupCreation: group,
            companyId: this.envCompanyId,
        }).then((res) => {
            this.currentTab = "groups";
            this.reset();
            this.$store.commit("alerts/displaySuccess", {
                msg : this.$i18n.t("actions.created"),
            });
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        }).finally(() => {
            this.loadingCreate = false;
        });
    }

    private close() {
        this.statsDialog = false;
        this.groupDialog = false;
        this.userGroupSelectable = [];
        this.userGroupType = null;
        this.incrementPagination = 0;
        ++this.manageRightsKey;
        this.deselectAllUsers();
    }

    private selectContact(contact) {
        const selectedUsers = this.selectedUsers.slice(0);
        if (selectedUsers.includes(contact)) {
            const index = selectedUsers.findIndex((item) => {
                const user: any = item;
                return user.id === contact.id;
            });
            selectedUsers.splice(index, 1);
            this.selectedUsers = selectedUsers;
        } else {
            selectedUsers.push(contact);
            this.selectedUsers = selectedUsers;
        }
    }

    private async getSelectableUserGroup({search, page, itemsPerPage}:
    { search: string, page: number, itemsPerPage: number }):
     Promise<{ count: number; rows: any[] }>  {
        if (this.userGroupType === "contacts") {
            return this.getContacts({
                search,
                page,
                itemsPerPage,
            }).then((res) => {
                this.userGroupSelectable = res.users;
                return {count: res.count, rows : this.userGroupSelectable};
            });
        } else {
            return this.getSubordinates({
                search,
                page,
                itemsPerPage,
            }).then((res) => {
                this.userGroupSelectable = res.users;
                return {count: res.count, rows : this.userGroupSelectable};
            });
        }
    }

    private getUsers(users: any): any {
        return users.map((u) => ({
            ...u,
            firstName: u.Contact?.firstName || u.firstName,
            lastName: u.Contact?.lastName || u.lastName,
            specificCompanyName: u.Contact?.specificCompanyName || u.specificCompanyName,
        }));
    }
}
